import React, {
  useContext,
  createContext,
  useEffect,
  useRef,
  useReducer,
  useMemo
} from 'react';
import { createContainer } from 'react-tracked';
import { eventEmitter, events } from '../utils';

const NOOP = () => {};

export const INITIAL_STATE = {
  meetingId: null,
  meeting: null,
  baseUrl: null,
  localParticipant: null,
  mainParticipant: null,
  activeSpeakerId: null,
  presenterId: null,
  participants: new Map(),
  characters: new Map(),
  pinnedParticipants: new Map(),
  connections: new Map(),
  localMicOn: false,
  localWebcamOn: false,
  localScreenShareOn: false,
  messages: [],
  isMeetingJoined: false,
  isRecording: false,
  isLiveStreaming: false,
  isHls: false,
  recordingState: 'RECORDING_STOPPED',
  livestreamState: 'LIVESTREAM_STOPPED',
  hlsState: 'HLS_STOPPED',
  hlsUrls: { downstreamUrl: null, playbackHlsUrl: null, livestreamUrl: null },
  transcriptionState: 'TRANSCRIPTION_STOPPED',
  translationState: 'TRANSLATION_STOPPED',
  selectedCameraDevice: null,
  selectedMicrophoneDevice: null
};

const reducer = (state, patch) => {
  if (patch === 'RESET') {
    return { ...INITIAL_STATE };
  }

  const changes = typeof patch === 'function' ? patch(state) : patch;

  const hasRealChange = Object.keys(changes).some(k => state[k] !== changes[k]);
  if (!hasRealChange) return state;

  return { ...state, ...changes };
};

const {
  Provider: MeetingStateProviderBase,
  useTrackedState: useMeetingState,
  useSelector: useMeetingSelector,
  useUpdate: useMeetingDispatch
} = createContainer(() => useReducer(reducer, INITIAL_STATE));

export const MeetingStateProvider = ({ children }) => (
  <MeetingStateProviderBase>{children}</MeetingStateProviderBase>
);

export const MeetingMethodsContext = createContext(null);

// Holds a ref that always points at the latest committed meeting state.
// Shared with useMeeting() so it can return fresh values for lazy reads
// (event handlers, effects, timeouts) without forcing every consumer to
// subscribe to the whole state. A single component (StateRefSync) pays the
// full-state subscription cost and keeps this ref up to date.
export const MeetingLiveStateContext = createContext(null);

const useStableEvent = (eventName, handler) => {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });
  useEffect(() => {
    const stable = (...args) => handlerRef.current(...args);
    eventEmitter.on(eventName, stable);
    return () => eventEmitter.off(eventName, stable);
  }, [eventName]);
};

export {
  useMeetingState,
  useMeetingSelector,
  useMeetingDispatch,
  useStableEvent
};

/**
 *
 * @param {{
 *  onParticipantJoined?: (participant: Participant) => void,
 *  onParticipantLeft?: (participant: Participant) => void,
 *  onSpeakerChanged?: (activeSpeakerId: string?) => void,
 *  onPresenterChanged?: (presenterId: string?) => void,
 *  onMainParticipantChanged?: Function,
 *  onEntryRequested?: ({
 *    participantId,
 *    name,
 *    allow,
 *    deny
 *  }:{
 *    participantId: string,
 *    name: string,
 *    allow: Function,
 *    deny: Function
 *  }) => void,
 *  onEntryResponded?: ({
 *    participantId,
 *    decision
 *  }:{
 *    participantId: string,
 *    decision: string
 *  }) => void,
 *  onPausedAllStreams?: ({
 *    kind,
 *  }:{
 *    kind: "audio" | "video" | "share" | "shareAudio" | "all",
 *  }) => void,
 *  onResumedAllStreams?: ({
 *    kind,
 *  }:{
 *    kind: "audio" | "video" | "share" | "shareAudio" | "all",
 *  }) => void,
 *  onRecordingStarted?: Function,
 *  onRecordingStopped?: Function,
 *  onData?: Function,
 *  onMeetingJoined?: Function,
 *  onMeetingLeft?: Function,
 *  onLiveStreamStarted?: Function,
 *  onLiveStreamStopped?: Function,
 *  onWebcamRequested?: ({
 *    participantId,
 *    accept,
 *    reject
 *  }:{
 *    participantId: string,
 *    accept: Function,
 *    reject: Function
 *  }) => void,
 *  onMicRequested?: ({
 *    participantId,
 *    accept,
 *    reject
 *  }:{
 *    participantId: string,
 *    accept: Function,
 *    reject: Function
 *  }) => void,
 *  onPinStateChanged?: Function,
 *  onConnectionOpen?: Function,
 *  onConnetionClose?: Function,
 *  onSwitchMeeting?: Function,
 *  onError?: ({
 *    code,
 *    message
 *  }:{
 *    code: string,
 *    message: string
 *  }) => void,
 *  onHlsStarted?: ({
 *    downstreamUrl,
 *  }:{
 *    downstreamUrl: string,
 *  }) => void,
 *  onHlsStopped?: Function,
 *  onHlsStateChanged?: ({
 *    status,
 *    downstreamUrl,
 *    playbackHlsUrl,
 *    livestreamUrl
 *  }:{
 *    status: "HLS_STARTING" | "HLS_STARTED" | "HLS_PLAYABLE" | "HLS_STOPPING" | "HLS_STOPPING",
 *    downstreamUrl: string,
 *    playbackHlsUrl: string,
 *    livestreamUrl: string
 *  }) => void,
 *  onRecordingStateChanged?: ({
 *    status
 *  }:{
 *    status: "RECORDING_STARTING" | "RECORDING_STARTED" | "RECORDING_STOPPING" | "RECORDING_STOPPING",
 *  }) => void,
 *  onLivestreamStateChanged?: ({
 *     status: "LIVESTREAM_STARTING" | "LIVESTREAM_STARTED" | "LIVESTREAM_STOPPING" | "LIVESTREAM_STOPPING",
 *  }:{
 *    status: string
 *  }) => void,
 *  onMeetingStateChanged?: ({
 *    state
 *  }:{
 *    state: string
 *  }) => void,
 *  onMediaRelayStarted?: ({
 *    meetingId
 *  }: {
 *    meetingId: string
 *  }) => void,
 *  onMediaRelayStopped?: ({
 *    meetingId,
 *    reason
 *  }: {
 *    meetingId: string,
 *    reason: string
 *  }) => void,
 *  onMediaRelayError?: ({
 *    meetingId,
 *    error
 *  }: {
 *    meetingId: string,
 *    error: string
 *  }) => void,
 *  onMediaRelayRequestResponse?: ({
 *    decision,
 *    decidedBy,
 *    meetingId
 *  }: {
 *    decision: "accepted" | "rejected",
 *    decidedBy: string,
 *    meetingId: string
 *  }) => void,
 *  onMediaRelayRequestReceived?: ({
 *    participantId,
 *    meetingId,
 *    displayName,
 *    accept,
 *    reject
 *  }: {
 *    participantId: string,
 *    meetingId: string,
 *    displayName: string,
 *    accept: () => void,
 *    reject: () => void
 *  }) => void,
 *  onQualityLimitation?: ({
 *    type,
 *    state,
 *    timestamp,
 *  }: {
 *    type: "congestion" | "bandwidth" | "cpu",
 *    state: "detected" | "resolved",
 *    timestamp: number,
 *  }) => void,
 *  onAudioInputSilence?: ({
 *    timestamp,
 *    devicelabel,
 *    state,
 *
 *  }: {
 *    peerId: string,
 *    devicelabel?: string,
 *    state: "detected" | "resolved",
 *    timestamp: number,
 *
 *  }) => void,
 *  onCodecChanged?: ({
 *    previousCodec,
 *    currentCodec,
 *    unsupportedRemoteCodec,
 *    kind,
 *    causedBy
 *  }: {
 *    previousCodec: string,
 *    currentCodec: string,
 *    unsupportedRemoteCodec: string,
 *    kind: "video",
 *    causedBy?: object
 *  }) => void,
 *}}
 * @returns {{
 *  meetingId: string,
 *  meeting: Meeting,
 *  localParticipant: Participant,
 *  activeSpeakerId: string,
 *  participants: Map<string, Participant>,
 *  characters: Map<string, Character>;
 *  pinnedParticipants: Map<string, {cam:boolean, share:boolean}>,
 *  presenterId?: string,
 *  localMicOn: boolean,
 *  localWebcamOn: boolean,
 *  isRecording: boolean,
 *  recordingState: string,
 *  livestreamState: string,
 *  hlsState: string,
 *  hlsUrls: {
 *    downstreamUrl: string,
 *    playbackHlsUrl: string,
 *    livestreamUrl: string
 *  },
 *  localScreenShareOn: boolean,
 *  connections: Map<string, Connection>,
 *  join: () => Promise<void>
 *  leave: () => Promise<void>
 *  end: () => Promise<void>
 *  unmuteMic: (customAudioTrack?: MediaStream | undefined) => Promise<void>
 *  muteMic: () => Promise<void>
 *  toggleMic: (customAudioTrack?: MediaStream | undefined) => Promise<void>
 *  enableWebcam: (customVideoTrack?: MediaStream | undefined) => Promise<void>
 *  disableWebcam: () => Promise<void>
 *  send: (payload: string | Blob | ArrayBuffer | ArrayBufferView,options?: {reliability?: "RELIABLE" | "UNRELIABLE"}) => Promise<boolean>
 *  toggleWebcam: (customVideoTrack?: MediaStream | undefined) => Promise<void>
 *  enableScreenShare: (customScreenShareTrack?: MediaStream | undefined) => Promise<void>
 *  disableScreenShare: () => Promise<void>
 *  toggleScreenShare: (customScreenShareTrack?: MediaStream | undefined) => Promise<void>
 *  enableAdaptiveSubscription: () => Promise<void>
 *  disableAdaptiveSubscription: () => Promise<void>
 *  pauseAllStreams: (kind?: "audio" | "video" | "share" | "shareAudio" | "all") => Promise<void>
 *  resumeAllStreams: (kind?: "audio" | "video" | "share" | "shareAudio" | "all") => Promise<void>
 *  startRecording: (webhookUrl?: string, awsDirPath?: string, config?: {
 *    layout: {
 *      type: "GRID" | "SPOTLIGHT" | "SIDEBAR";
 *      priority: "SPEAKER" | "PIN";
 *      gridSize: number;
 *    },
 *    orientation: "landscape"  | "portrait",
 *    theme: "DEFAULT" | "DARK" | "LIGHT",
 *    quality: "low" | "med" | "high",
 *    mode: "video-and-audio" | "audio",
 *   },
 *  transcription?: {
 *    enabled: boolean,
 *    summary?: {
 *      enabled: boolean,
 *      prompt?: string
 *    },
 *    language?: string
 *  }) => Promise<void>
 *  stopRecording: () => Promise<void>
 *  switchTo: ({
 *   meetingId,
 *   token
 *   }: {
 *   meetingId: string;
 *   token?: string;
 *   }) => Promise<void>
 *  startLivestream: (outputs: Array<{
 *       url: string;
 *       streamKey: string;
 *   }>,
 *   config?: {
 *    layout?: {
 *      type?: "GRID" | "SPOTLIGHT" | "SIDEBAR";
 *      priority?: "SPEAKER" | "PIN";
 *      gridSize?: number;
 *    },
 *    theme?: "DARK" | "LIGHT" | "DEFAULT",
 *    recording?: {
 *      enabled: boolean
 *    }
 *   }) => Promise<void>
 *  stopLivestream: () => Promise<void>
 *  startHls: (config?: {
 *    layout?: {
 *      type?: "GRID" | "SPOTLIGHT" | "SIDEBAR";
 *      priority?: "SPEAKER" | "PIN";
 *      gridSize?: number;
 *    },
 *    theme?: "DARK" | "LIGHT" | "DEFAULT",
 *    mode?: "video-and-audio" | "audio",
 *    quality?: "low" | "med" | "high",
 *    recording?: {
 *      enabled: boolean
 *    }
 *   },
 *  transcription?: {
 *    enabled: boolean,
 *    summary?: {
 *      enabled: boolean,
 *      prompt?: string
 *    }
 *  }) => Promise<void>
 *  stopHls: () => Promise<void>
 *  getMics: () => Promise<{
 *       deviceId: string;
 *       label: string;
 *   }[]>
 *  getWebcams: () => Promise<{
 *       deviceId: string;
 *       label: string;
 *       facingMode: "environment" | "front"
 *   }[]>
 *  changeMic: (object: string | MediaStream) => Promise<void>
 *  changeMode: (mode: 'SEND_AND_RECV' | 'SIGNALLING_ONLY' | 'RECV_ONLY') => Promise<void>
 *  changeWebcam: (object: string | MediaStream) => Promise<void>
 *  respondEntry: (participantId: string, decision: "allow" | "deny") => Promise<void>
 *  replaceWebcamStream: (stream: MediaStream) => Promise<void>
 *  requestMediaRelay: (options: {
 *    destinationMeetingId: string;
 *    token?: string;
 *    kinds?: Array<"audio" | "video" | "share" | "share_audio">;
 *  }) => Promise<void>
 *  stopMediaRelay: (destinationMeetingId: string) => Promise<void>
 *
 * }}
 */
export const useMeeting = ({
  onParticipantJoined = NOOP,
  onParticipantLeft = NOOP,
  onSpeakerChanged = NOOP,
  onPresenterChanged = NOOP,
  onMainParticipantChanged = NOOP,
  onEntryRequested = NOOP,
  onEntryResponded = NOOP,
  onPausedAllStreams = NOOP,
  onResumedAllStreams = NOOP,
  onRecordingStarted = NOOP,
  onRecordingStopped = NOOP,
  onData = NOOP,
  onMeetingJoined = NOOP,
  onMeetingLeft = NOOP,
  onLiveStreamStarted = NOOP,
  onLiveStreamStopped = NOOP,
  onWebcamRequested = NOOP,
  onMicRequested = NOOP,
  onPinStateChanged = NOOP,
  onConnectionOpen = NOOP,
  onConnetionClose = NOOP,
  onSwitchMeeting = NOOP,
  onError = NOOP,
  onHlsStarted = NOOP,
  onHlsStopped = NOOP,
  onHlsStateChanged = NOOP,
  onRecordingStateChanged = NOOP,
  onLivestreamStateChanged = NOOP,
  onMeetingStateChanged = NOOP,
  onParticipantModeChanged = NOOP,
  onCharacterJoined = NOOP,
  onCharacterLeft = NOOP,
  onMediaRelayStarted = NOOP,
  onMediaRelayStopped = NOOP,
  onMediaRelayError = NOOP,
  onMediaRelayRequestResponse = NOOP,
  onMediaRelayRequestReceived = NOOP,
  onQualityLimitation = NOOP,
  onAudioInputSilence = NOOP,
  onCodecChanged = NOOP
} = {}) => {
  useStableEvent(events['participant-joined'], onParticipantJoined);
  useStableEvent(events['participant-left'], onParticipantLeft);
  useStableEvent(events['speaker-changed'], onSpeakerChanged);
  useStableEvent(events['presenter-changed'], onPresenterChanged);
  useStableEvent(events['main-participant-changed'], onMainParticipantChanged);
  useStableEvent(events['entry-requested'], onEntryRequested);
  useStableEvent(events['entry-responded'], onEntryResponded);
  useStableEvent(events['paused-all-streams'], onPausedAllStreams);
  useStableEvent(events['resumed-all-streams'], onResumedAllStreams);
  useStableEvent(events['recording-started'], onRecordingStarted);
  useStableEvent(events['recording-stopped'], onRecordingStopped);
  useStableEvent(events['data'], onData);
  useStableEvent(events['meeting-joined'], onMeetingJoined);
  useStableEvent(events['meeting-left'], onMeetingLeft);
  useStableEvent(events['live-stream-started'], onLiveStreamStarted);
  useStableEvent(events['live-stream-stopped'], onLiveStreamStopped);
  useStableEvent(events['webcam-requested'], onWebcamRequested);
  useStableEvent(events['mic-requested'], onMicRequested);
  useStableEvent(events['pin-state-changed'], onPinStateChanged);
  useStableEvent(events['connection-open'], onConnectionOpen);
  useStableEvent(events['connection-close'], onConnetionClose);
  useStableEvent(events['switch-meeting'], onSwitchMeeting);
  useStableEvent(events['error'], onError);
  useStableEvent(events['hls-started'], onHlsStarted);
  useStableEvent(events['hls-stopped'], onHlsStopped);
  useStableEvent(events['hls-state-changed'], onHlsStateChanged);
  useStableEvent(events['recording-state-changed'], onRecordingStateChanged);
  useStableEvent(events['livestream-state-changed'], onLivestreamStateChanged);
  useStableEvent(events['meeting-state-changed'], onMeetingStateChanged);
  useStableEvent(events['participant-mode-changed'], onParticipantModeChanged);
  useStableEvent(events['character-joined'], onCharacterJoined);
  useStableEvent(events['character-left'], onCharacterLeft);
  useStableEvent(events['media-relay-started'], onMediaRelayStarted);
  useStableEvent(events['media-relay-stopped'], onMediaRelayStopped);
  useStableEvent(events['media-relay-error'], onMediaRelayError);
  useStableEvent(
    events['media-relay-request-response'],
    onMediaRelayRequestResponse
  );
  useStableEvent(
    events['media-relay-request-received'],
    onMediaRelayRequestReceived
  );
  useStableEvent(events['quality-limitation'], onQualityLimitation);

  useStableEvent(events['audio-input-silence'], onAudioInputSilence);
  useStableEvent(events['codec-changed'], onCodecChanged);

  // react-tracked tracks which properties consumers access during render
  // and only re-renders for those. `state` is that tracked proxy.
  const state = useMeetingState();
  const methods = useContext(MeetingMethodsContext);
  // Ref to the latest committed state (kept fresh by StateRefSync). Used to
  // resolve state VALUES so lazy reads aren't stale, while `state` above is
  // still touched to preserve render-time subscription.
  const liveStateRef = useContext(MeetingLiveStateContext);

  // We return the tracked state proxy itself, plus a stable methodsRef
  // so consumers can access methods without triggering state subscriptions.
  // Consumers should destructure: const { participants, join } = useMeeting()
  // The tracked proxy's get trap will fire for 'participants' (tracked),
  // and fall through to the prototype for 'join' (not tracked = no re-render).
  const methodsRef = useRef(methods);
  methodsRef.current = methods;

  // Create a proxy that delegates state reads to the tracked proxy (preserving tracking)
  // and method reads to the methods context (no tracking / no re-render).
  // Wrap in useMemo so the returned object has a stable reference across renders
  // (only changes when the tracked state reference changes).
  return useMemo(
    () =>
      new Proxy(state, {
        get(target, prop, receiver) {
          if (prop in INITIAL_STATE) {
            // Touch the tracked proxy so react-tracked subscribes this
            // component to `prop` when read during render (re-render on change).
            const trackedValue = target[prop];
            // Return the latest committed value from the live ref so lazy reads
            // (event handlers, effects, timeouts) never return a value captured
            // from an earlier render. Falls back to the tracked value before the
            // ref is first populated.
            const live = liveStateRef && liveStateRef.current;
            return live ? live[prop] : trackedValue;
          }
          const m = methodsRef.current;
          if (m && prop in m) return m[prop];
          return Reflect.get(target, prop, receiver);
        },
        has(target, prop) {
          return (
            prop in INITIAL_STATE ||
            !!(methodsRef.current && prop in methodsRef.current)
          );
        },
        ownKeys() {
          const stateKeys = Object.keys(INITIAL_STATE);
          const methodKeys = Object.keys(methodsRef.current ?? {});
          return [...new Set([...stateKeys, ...methodKeys])];
        },
        getOwnPropertyDescriptor() {
          return { configurable: true, enumerable: true, writable: false };
        }
      }),
    [state, liveStateRef]
  );
};

export const MeetingConsumer = ({ children, ...callbacks }) => {
  const meetingValue = useMeeting(callbacks);
  return <React.Fragment>{children(meetingValue)}</React.Fragment>;
};
